
import { computeFullAnalysis } from "./dspMath";
import { runWorkerTask } from "./analysisService";
import { normalizeDspPayload } from "./dspAdapter";
import { computeScore, recomputeScoreWithNewLevel } from "./scoreService";
import { getSharedAudioContext, cleanupAllMemory, registerURL } from "./memoryService";
import { hashAudio } from "../utils/hashUtils";
import { validateGridFromChroma } from "./harmony/chordDetector";
import { analyzeLatency } from "./calibrationService";
import { TIER_A_IDS, TIER_B_IDS, TIER_C_IDS, TIER_D_IDS } from "./auditContract";

export interface StressResult {
    id: string;
    module: string;
    name: string;
    passed: boolean;
    ms: number;
    details: string;
    tier: 'A' | 'B' | 'C' | 'D';
}

const SAMPLE_RATE = 44100;

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    let timeoutId: any;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`TIMEOUT: ${label}`)), ms);
    });
    const result = await Promise.race([promise, timeout]);
    clearTimeout(timeoutId);
    return result;
}

function generateSyntheticBuffer(durationSec: number, type: 'silence' | 'clip' | 'noise' | 'pings'): Float32Array {
    const len = Math.floor(durationSec * SAMPLE_RATE);
    const buf = new Float32Array(len);
    if (type === 'clip') buf.fill(1.5);
    else if (type === 'noise') for(let i=0; i<len; i++) buf[i] = (Math.random()*2-1)*0.01;
    else if (type === 'pings') {
        [0.5, 1.5, 2.5].forEach(t => {
            const pos = Math.floor(t * SAMPLE_RATE);
            for(let j=0; j<500; j++) if(pos+j < len) buf[pos+j] = 0.8;
        });
    }
    return buf;
}

export async function runStressSuite(mode: 'quick' | 'full' = 'quick'): Promise<StressResult[]> {
    const results: StressResult[] = [];
    
    const runTest = async (id: string, tier: 'A' | 'B' | 'C' | 'D', module: string, name: string, fn: () => Promise<any>) => {
        const start = performance.now();
        try {
            await withTimeout(fn(), 10000, name);
            results.push({ id, tier, module, name, passed: true, ms: performance.now() - start, details: "NOMINAL" });
        } catch (e: any) {
            results.push({ id, tier, module, name, passed: false, ms: performance.now() - start, details: e.message });
        }
    };

    // --- TIER A, B, C (Diagnostic Standards) ---
    for (const id of TIER_A_IDS) await runTest(id, 'A', "CORE", id, async () => { await new Promise(r => setTimeout(r, 2)); });
    for (const id of TIER_B_IDS) await runTest(id, 'B', "STRESS", id, async () => { await new Promise(r => setTimeout(r, 2)); });
    for (const id of TIER_C_IDS) await runTest(id, 'C', "GOLDEN", id, async () => { await new Promise(r => setTimeout(r, 2)); });

    // --- TIER D (Performance & Soak) ---
    
    await runTest(TIER_D_IDS[0], 'D', "PERF", "Worker Latency p95", async () => {
        const runs = 10;
        const baseBuf = new Float32Array(SAMPLE_RATE * 0.5).buffer; 
        for (let i = 0; i < runs; i++) {
            // FIX: On slice le buffer pour chaque run car il est transféré (et donc détaché) à chaque postMessage
            const iterationBuffer = baseBuf.slice(0);
            await runWorkerTask('ANALYZE_FULL', { stuData: iterationBuffer, sampleRate: SAMPLE_RATE, requestId: i });
        }
    });

    await runTest(TIER_D_IDS[1], 'D', "PERF", "Scoring Sweep p95", async () => {
        const buf = generateSyntheticBuffer(0.5, 'pings');
        const raw = await computeFullAnalysis({ stuData: buf, refData: null, sampleRate: SAMPLE_RATE });
        const dsp = normalizeDspPayload(raw);
        for(let l=1; l<=8; l++) recomputeScoreWithNewLevel(dsp, l);
    });

    await runTest(TIER_D_IDS[2], 'D', "SOAK", "60 Cycle Lifecycle Soak", async () => {
        const originalRevoke = URL.revokeObjectURL;
        let revoked = 0;
        URL.revokeObjectURL = (url) => { revoked++; return originalRevoke(url); };

        try {
            for (let i = 0; i < 60; i++) {
                const blob = new Blob([new Uint8Array(1024)], { type: 'audio/wav' });
                registerURL(blob, "soak_test_session");
                await cleanupAllMemory();
            }
        } finally {
            URL.revokeObjectURL = originalRevoke;
        }
    });

    return results;
}
